/**
 * Tracks in-flight lazy route chunk downloads, independent of React rendering.
 *
 * Tying the "loading" signal to the actual `import()` promise (see
 * {@link ./lazyWithProgress}) — rather than to a Suspense fallback rendering —
 * means the progress bar fires for *every* chunk load at *any* Suspense
 * boundary, and is unaffected by react-router v7's default `startTransition`
 * (which keeps the old page rendered, so nested/un-keyed fallbacks never mount).
 */
type Listener = () => void;

let pendingChunks = 0;
const listeners = new Set<Listener>();

const notify = (): void => {
  listeners.forEach((listener) => listener());
};

export const chunkLoadTracker = {
  begin(): void {
    pendingChunks += 1;
    notify();
  },
  end(): void {
    pendingChunks = Math.max(0, pendingChunks - 1);
    notify();
  },
  /** Stable snapshot for `useSyncExternalStore`. */
  getIsLoading: (): boolean => pendingChunks > 0,
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};
